/**
 * `ModuleLiegenschaften` — the Stammdaten → Liegenschaften detail screen of the
 * HERO product mock (1:1 with the prototype `showLiegenschaft` block). Shows the
 * "Ahornweg 5" property: identity header, an inner property sub-nav
 * (Übersicht/Einheiten/…/Bankkonten), three stat cards, the Adresse card and the
 * Stammdaten grid. The module sidebar is the shared `StammdatenSidebar`.
 */
import { ld } from "@/lib/theme/light-dark";
import {
  Building,
  Building2,
  FileText,
  Folder,
  Gauge,
  HardHat,
  Landmark,
  LayoutGrid,
  MapPin,
  Pencil,
} from "lucide-react";
import { useTranslations } from "next-intl";
import type { CSSProperties, ReactNode } from "react";

import { StammdatenSidebar } from "../../hero-stammdaten-sidebar";
import { H, MODULE_HEIGHT } from "../../hero-tokens";
import type { ModuleProps } from "../module-registry";

/** Mono section eyebrow used by the Adresse + Stammdaten cards. */
const MONO_LABEL: CSSProperties = {
  fontFamily: H.label,
  fontSize: 10,
  letterSpacing: "0.12em",
  color: ld("rgba(11,15,25,0.4)", "rgba(250,250,250,0.4)"),
  textTransform: "uppercase",
};

/** Inert inner-nav row (icon + label, optional trailing count). */
function PropNavRow(props: {
  readonly icon: ReactNode;
  readonly label: string;
  readonly count?: string;
}): ReactNode {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 11px",
        borderRadius: 8,
        fontSize: 13.5,
        color: ld("rgba(11,15,25,0.7)", "rgba(250,250,250,0.7)"),
      }}
    >
      <span style={{ color: ld("#6B7280", "#a6aaaf"), display: "inline-flex" }}>
        {props.icon}
      </span>
      {props.label}
      {props.count !== undefined ? (
        <span
          style={{
            marginLeft: "auto",
            fontSize: 12,
            color: ld("rgba(11,15,25,0.34)", "rgba(250,250,250,0.34)"),
          }}
        >
          {props.count}
        </span>
      ) : null}
    </div>
  );
}

/** A single stat card (icon tile + big number + label). */
function StatCard(props: {
  readonly icon: ReactNode;
  readonly value: string;
  readonly label: string;
}): ReactNode {
  return (
    <div
      style={{
        flex: 1,
        border: `1px solid ${ld("#ECEEF1", "#2e3135")}`,
        borderRadius: 12,
        padding: "16px 18px",
        display: "flex",
        alignItems: "center",
        gap: 14,
      }}
    >
      <span
        style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          background: ld("#F4F5F7", "#272a2e"),
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: ld("rgba(11,15,25,0.55)", "rgba(250,250,250,0.55)"),
        }}
      >
        {props.icon}
      </span>
      <div style={{ display: "flex", flexDirection: "column" }}>
        <span style={{ fontSize: 22, fontWeight: 700, lineHeight: 1 }}>
          {props.value}
        </span>
        <span style={{ fontSize: 12.5, color: H.inkFaint, marginTop: 3 }}>
          {props.label}
        </span>
      </div>
    </div>
  );
}

/** A single Stammdaten grid field (label over value). */
function StammField(props: {
  readonly label: string;
  readonly value: string;
}): ReactNode {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <span style={{ fontSize: 12, color: ld("rgba(11,15,25,0.45)", "rgba(250,250,250,0.45)") }}>
        {props.label}
      </span>
      <span style={{ fontSize: 13.5, fontWeight: 500, color: H.ink }}>
        {props.value}
      </span>
    </div>
  );
}

export function ModuleLiegenschaften(props: ModuleProps): ReactNode {
  const t = useTranslations("marketing.moduleLiegenschaften");

  return (
    <div style={{ display: "flex", height: MODULE_HEIGHT }}>
      <StammdatenSidebar active="liegenschaften" setActive={props.setActive} />

      <div
        style={{ flex: 1, minWidth: 0, padding: "20px 22px", overflow: "auto" }}
      >
        {/* Identity header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 18,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: ld("#F1F2F4", "#2b2e32"),
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: H.inkMute,
              }}
            >
              <Building2 width={20} height={20} strokeWidth={1.7} />
            </span>
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <span style={{ fontSize: 18, fontWeight: 700 }}>
                {t("ahornweg5")}
              </span>
              <span style={{ fontSize: 13, color: H.inkFaint }}>
                {t("mietverwaltung50667Koeln")}
              </span>
            </div>
          </div>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              border: `1px solid ${ld("#E7E9ED", "#32363a")}`,
              borderRadius: 9,
              padding: "8px 14px",
              fontSize: 13,
              fontWeight: 500,
              color: H.ink,
              cursor: "pointer",
            }}
          >
            <Pencil width={14} height={14} strokeWidth={1.8} />
            {t("bearbeiten")}
          </span>
        </div>

        <div style={{ display: "flex", gap: 22, alignItems: "flex-start" }}>
          {/* Inner property sub-nav */}
          <div
            style={{
              width: 182,
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "8px 11px",
                borderRadius: 8,
                fontSize: 13.5,
                fontWeight: 600,
                color: H.accent,
                background: H.accentSoft,
              }}
            >
              <LayoutGrid width={16} height={16} strokeWidth={1.9} />
              {t("uebersicht")}
            </div>
            <PropNavRow
              icon={<Building width={16} height={16} strokeWidth={1.8} />}
              label={t("einheiten")}
              count="12"
            />
            <PropNavRow
              icon={<FileText width={16} height={16} strokeWidth={1.8} />}
              label={t("vertraege")}
              count="11"
            />
            <PropNavRow
              icon={<Gauge width={16} height={16} strokeWidth={1.8} />}
              label={t("zaehler")}
              count="3"
            />
            <PropNavRow
              icon={<HardHat width={16} height={16} strokeWidth={1.8} />}
              label={t("dienstleister")}
            />
            <PropNavRow
              icon={<Folder width={16} height={16} strokeWidth={1.8} />}
              label={t("dokumente")}
            />
            <div
              style={{ height: 1, background: ld("#EEF0F3", "#2c2f33"), margin: "8px 6px" }}
            />
            <PropNavRow
              icon={<Landmark width={16} height={16} strokeWidth={1.8} />}
              label={t("bankkonten")}
            />
          </div>

          {/* Content column */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
              <StatCard
                icon={<Building width={19} height={19} strokeWidth={1.8} />}
                value="12"
                label={t("einheiten")}
              />
              <StatCard
                icon={<FileText width={19} height={19} strokeWidth={1.8} />}
                value="11"
                label={t("vertraege")}
              />
              <StatCard
                icon={<Gauge width={19} height={19} strokeWidth={1.8} />}
                value="3"
                label={t("zaehler")}
              />
            </div>

            {/* Adresse card */}
            <div
              style={{
                border: `1px solid ${ld("#ECEEF1", "#2e3135")}`,
                borderRadius: 12,
                padding: "16px 18px",
                marginBottom: 14,
              }}
            >
              <div style={{ ...MONO_LABEL, marginBottom: 10 }}>
                {t("adresse")}
              </div>
              <div
                style={{ display: "flex", alignItems: "flex-start", gap: 10 }}
              >
                <span
                  style={{
                    color: H.inkFaint,
                    display: "inline-flex",
                    marginTop: 1,
                  }}
                >
                  <MapPin width={16} height={16} strokeWidth={1.8} />
                </span>
                <div style={{ fontSize: 14, lineHeight: 1.5, color: H.ink }}>
                  {t.rich("postanschrift", {
                    br: () => <br />,
                    muted: (chunks) => (
                      <span style={{ color: H.inkFaint }}>{chunks}</span>
                    ),
                  })}
                </div>
              </div>
            </div>

            {/* Stammdaten card */}
            <div
              style={{
                border: `1px solid ${ld("#ECEEF1", "#2e3135")}`,
                borderRadius: 12,
                padding: "16px 18px",
              }}
            >
              <div style={{ ...MONO_LABEL, marginBottom: 14 }}>
                {t("stammdaten")}
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4,1fr)",
                  gap: 18,
                }}
              >
                <StammField label={t("liegenschaftId")} value="LG-4021" />
                <StammField label={t("typ")} value={t("mietverwaltung")} />
                <StammField label={t("baujahr")} value="1998" />
                <StammField label={t("verwalter")} value="Nina Hartmann" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
